/**
 * Home Screen shortcut that reopens the original room scan.
 *
 * The app strips the scan parameters from the address bar once they are
 * consumed, so the scan link is remembered for this tab only and put back in
 * the address bar while the guest adds the shortcut. Android Chrome also uses
 * the manifest's start_url for browser shortcuts, so its manifest is suspended
 * while the guide is open. A marker tells a later launch apart from a fresh QR
 * scan, which is otherwise the same URL.
 */
const ORIGINAL_SCAN_KEY = "gepOriginalScanUrl";
const NAME_PENDING_KEY = "gepShortcutNamePending";
const NAME_SKIPPED_KEY = "gepShortcutNameSkipped";

export const SHORTCUT_PARAM = "gepsrc";
export const SHORTCUT_VALUE = "shortcut";

function isScanSearch(search: URLSearchParams): boolean {
  return search.has("vcenc") || search.has("vcuser_room_number");
}

/**
 * Edit the raw query string without re-serialising it: `vcenc` is base64 and
 * URLSearchParams would turn a literal `+` into a space.
 */
function withoutMarker(url: URL): void {
  const pairs = url.search
    .replace(/^\?/, "")
    .split("&")
    .filter((pair) => pair && !pair.startsWith(`${SHORTCUT_PARAM}=`));
  url.search = pairs.length ? `?${pairs.join("&")}` : "";
}

export function isShortcutLaunch(search: URLSearchParams): boolean {
  return search.get(SHORTCUT_PARAM) === SHORTCUT_VALUE;
}

/** Call at boot, before the router removes the scan parameters. */
export function captureOriginalScanUrl(href: string = location.href): void {
  try {
    const url = new URL(href);
    if (!isScanSearch(url.searchParams)) return;
    withoutMarker(url);
    sessionStorage.setItem(ORIGINAL_SCAN_KEY, url.toString());
  } catch {
    // Storage blocked or a malformed URL: shortcuts simply fall back to Home.
  }
}

/** The remembered scan URL plus the shortcut marker, or null if unavailable. */
export function getShortcutUrl(): string | null {
  try {
    const stored = sessionStorage.getItem(ORIGINAL_SCAN_KEY);
    if (!stored) return null;
    const url = new URL(stored);
    // Never follow a tampered value off our own origin.
    if (url.origin !== location.origin) return null;
    withoutMarker(url);
    url.search = `${url.search ? `${url.search}&` : "?"}${SHORTCUT_PARAM}=${SHORTCUT_VALUE}`;
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * Show the shortcut URL in the address bar without reloading or navigating.
 * The router writes its own URL back on the next navigation.
 */
export function exposeShortcutUrl(): boolean {
  const target = getShortcutUrl();
  if (!target) return false;
  try {
    history.replaceState(history.state, "", target);
    return true;
  } catch {
    return false;
  }
}

/**
 * Android Chrome's native Create shortcut otherwise saves the PWA's generic
 * start_url instead of the scan URL in the address bar. Temporarily detach the
 * manifest while the shortcut guide is open, then restore the same nodes when
 * it closes. Keeping their attributes and position preserves normal WebAPK
 * installation after the guide and leaves the iOS install manifest untouched.
 */
export function suspendShortcutManifest(): () => void {
  const manifests = Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[rel~="manifest"]'),
    (link) => ({ link, parent: link.parentNode, next: link.nextSibling }),
  );
  for (const { link } of manifests) link.remove();

  let restored = false;
  return () => {
    if (restored) return;
    restored = true;
    // Restore from the end so adjacent manifest links keep their order too.
    for (const { link, parent, next } of manifests.reverse()) {
      if (link.isConnected || !parent?.isConnected) continue;
      parent.insertBefore(link, next?.parentNode === parent ? next : null);
    }
  };
}

/** True when this browser already holds a guest identity for the merchant. */
export function hasStoredGuestIdentity(
  apiKey: string,
  persistedUsername?: string | null,
): boolean {
  try {
    if (apiKey && localStorage.getItem(`${apiKey}___username`)) return true;
  } catch {
    // Treat unreadable storage like an empty one.
  }
  return Boolean(persistedUsername);
}

/** Set while a shortcut-created guest still has to give a name. */
export function markShortcutNamePending(): void {
  try {
    localStorage.setItem(NAME_PENDING_KEY, "1");
  } catch {
    // Without storage the name is only asked on this launch.
  }
}

export function isShortcutNamePending(): boolean {
  try {
    return localStorage.getItem(NAME_PENDING_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearShortcutNamePending(): void {
  try {
    localStorage.removeItem(NAME_PENDING_KEY);
  } catch {
    // Nothing to clear.
  }
}

/**
 * The name is only collected, so a guest who cannot save it (or declines) must
 * not be locked out. Skipping silences the prompt for this tab only; the name
 * stays pending and is asked again on a later visit.
 */
export function skipShortcutNameForVisit(): void {
  try {
    sessionStorage.setItem(NAME_SKIPPED_KEY, "1");
  } catch {
    // Without storage the prompt can reappear when Home remounts.
  }
}

export function isShortcutNameSkipped(): boolean {
  try {
    return sessionStorage.getItem(NAME_SKIPPED_KEY) === "1";
  } catch {
    return false;
  }
}
